import { TILE } from '../../shared/geo.js';
import { el } from './dom.js';

const SIZE = 212; // on-screen pixels
const TILE_PX = 256; // each tile is pre-drawn at this size
const SCALE = TILE_PX / (TILE * 10); // tile data is in decimetres

const LAND = ['#2c4a37', '#33503a', '#3d4f38', '#35553c', '#3a4048', '#41444c', '#3c3f46', '#33473b', '#2f6a44', '#3f4650', '#4a4d55'];
export const BLIP = {
  hospital: ['#e5484d', 'H'], clinic: ['#e5484d', '+'], pharmacy: ['#e5484d', 'Rx'], market: ['#f2b705', 'M'], food: ['#f2b705', 'C'], bar: ['#f08a24', 'b'], fuel: ['#f08a24', 'F'], bus: ['#3b9eff', 'B'], taxi: ['#3b9eff', 'T'],
  school: ['#8e7cf0', 'S'], university: ['#8e7cf0', 'U'], college: ['#8e7cf0', 'C'], library: ['#8e7cf0', 'L'], worship: ['#d6d1c4', '†'], bank: ['#46a758', '₣'], shop: ['#46a758', '$'],
  hotel: ['#e78fb3', 'h'], police: ['#3b6fd9', 'P'], townhall: ['#3b6fd9', 'Ω'], court: ['#3b6fd9', '§'], post: ['#3b6fd9', '✉'], office: ['#9aa3ad', 'O'], airport: ['#d6d1c4', 'A'], stadium: ['#46a758', 'o'], sight: ['#f2b705', '*'], peak: ['#9aa3ad', '▲'],
};
export const ROUTE_COLOUR = '#a98bff';

// The corner map: roads, buildings and water around the player, turned so
// that the way the camera looks is up, with markers for useful places.
export class Minimap {
  constructor(root, chunks, places) {
    this.chunks = chunks;
    this.places = places;
    this.range = 260; // metres from the centre to the edge
    this.canvas = el('canvas', { width: SIZE * 2, height: SIZE * 2, 'aria-label': 'Map of the area around you' });
    this.node = el('div', { id: 'minimap' }, this.canvas, el('div.north', null, 'N'));
    this.north = this.node.querySelector('.north');
    root.appendChild(this.node);
    this.ctx = this.canvas.getContext('2d');
    this.tiles = new Map(); // tile key -> pre-drawn canvas
    chunks.onTileBuilt = (t) => this.addTile(t);
    chunks.onTileRemoved = (t) => this.tiles.delete(t.key);
    this.near = [];
    this.nearAt = { x: Infinity, z: Infinity };
    this.marks = []; // { x, z, color }: places to go, kept on the rim when out of range
    this.route = null; // [[x, z], ...] the way to the goal, by road
  }

  addTile(t) {
    const c = document.createElement('canvas');
    c.width = TILE_PX;
    c.height = TILE_PX;
    const g = c.getContext('2d');
    g.fillStyle = '#2f3540';
    g.fillRect(0, 0, TILE_PX, TILE_PX);
    const d = t.data;
    if (d) {
      const path = (p, close) => {
        g.beginPath();
        g.moveTo(p[0] * SCALE, p[1] * SCALE);
        for (let i = 2; i < p.length; i += 2) g.lineTo(p[i] * SCALE, p[i + 1] * SCALE);
        if (close) g.closePath();
      };
      for (const l of d.l) {
        path(l.p, true);
        g.fillStyle = LAND[l.k] || LAND[3];
        g.fill();
      }
      g.fillStyle = '#3f7fa8';
      for (const a of d.a) {
        path(a.p, true);
        g.fill();
      }
      g.lineCap = 'round';
      g.lineJoin = 'round';
      g.strokeStyle = '#3f7fa8';
      for (const w of d.w) {
        path(w.p, false);
        g.lineWidth = Math.max(1.5, w.w * 10 * SCALE);
        g.stroke();
      }
      // minor roads first so main roads draw over them
      const order = d.r.slice().sort((a, b) => b.c - a.c);
      for (const r of order) {
        path(r.p, false);
        g.strokeStyle = r.s === 1 ? '#a8998a' : r.c <= 1 ? '#ffe08a' : '#f4f1ea';
        g.lineWidth = Math.max(r.c >= 6 ? 0.8 : 1.6, r.w * 10 * SCALE);
        g.stroke();
      }
      g.fillStyle = '#5a626d';
      for (const b of d.b) {
        path(b.p, true);
        g.fill();
      }
    }
    this.tiles.set(t.key, { canvas: c, tx: t.tx, tz: t.tz });
  }

  // x, z: the map centre. yaw: camera heading. facing: the player's heading.
  draw(x, z, yaw, facing) {
    // thirty times a second is plenty for a map; the frame has better uses for the time
    const now = performance.now();
    if (this.drewAt && now - this.drewAt < 32) return;
    this.drewAt = now;
    const g = this.ctx;
    const W = SIZE * 2;
    const k = W / (this.range * 2); // canvas pixels per metre
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.fillStyle = '#262b33';
    g.fillRect(0, 0, W, W);
    g.translate(W / 2, W / 2);
    const turn = yaw - Math.PI;
    g.rotate(turn);
    g.imageSmoothingEnabled = true;
    const reach = this.range * 1.5 + TILE;
    for (const t of this.tiles.values()) {
      const ox = t.tx * TILE - x;
      const oz = t.tz * TILE - z;
      if (ox > reach || oz > reach || ox + TILE < -reach || oz + TILE < -reach) continue;
      g.drawImage(t.canvas, ox * k, oz * k, TILE * k + 0.6, TILE * k + 0.6);
    }

    // the way to go, on the roads
    if (this.route && this.route.length > 1) {
      g.lineCap = 'round';
      g.lineJoin = 'round';
      g.beginPath();
      for (let i = 0; i < this.route.length; i++) {
        const px = (this.route[i][0] - x) * k;
        const pz = (this.route[i][1] - z) * k;
        if (i === 0) g.moveTo(px, pz);
        else g.lineTo(px, pz);
      }
      g.strokeStyle = 'rgba(20,23,28,0.7)';
      g.lineWidth = 11;
      g.stroke();
      g.strokeStyle = ROUTE_COLOUR;
      g.lineWidth = 7;
      g.stroke();
    }

    // markers stay upright: place them with the same rotation by hand
    g.setTransform(1, 0, 0, 1, W / 2, W / 2);
    if (Math.abs(x - this.nearAt.x) > 60 || Math.abs(z - this.nearAt.z) > 60) this.refreshNear(x, z);
    const cos = Math.cos(turn);
    const sin = Math.sin(turn);
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    for (const p of this.near) {
      const dx = (p.x - x) * k;
      const dz = (p.z - z) * k;
      const px = dx * cos - dz * sin;
      const py = dx * sin + dz * cos;
      if (Math.abs(px) > W / 2 || Math.abs(py) > W / 2) continue;
      const blip = BLIP[p.k];
      if (blip) {
        g.fillStyle = '#14171c';
        g.beginPath();
        g.arc(px, py, 13, 0, Math.PI * 2);
        g.fill();
        g.fillStyle = blip[0];
        g.beginPath();
        g.arc(px, py, 10.5, 0, Math.PI * 2);
        g.fill();
        g.fillStyle = '#14171c';
        g.font = `700 ${blip[1].length > 1 ? 11 : 15}px "Barlow Semi Condensed", Arial, sans-serif`;
        g.fillText(blip[1], px, py + 1);
      } else {
        g.font = '600 19px "Barlow Semi Condensed", Arial, sans-serif';
        g.lineWidth = 4;
        g.strokeStyle = 'rgba(20,23,28,0.85)';
        g.strokeText(p.n, px, py);
        g.fillStyle = '#f7eed9';
        g.fillText(p.n, px, py);
      }
    }

    // where to go next: a bright dot, held at the rim when it is off the map
    for (const m of this.marks) {
      const dx = (m.x - x) * k;
      const dz = (m.z - z) * k;
      let px = dx * cos - dz * sin;
      let py = dx * sin + dz * cos;
      const edge = W / 2 - 22;
      const far = Math.max(Math.abs(px), Math.abs(py));
      if (far > edge) {
        px *= edge / far;
        py *= edge / far;
      }
      g.fillStyle = '#14171c';
      g.beginPath();
      g.arc(px, py, 17, 0, Math.PI * 2);
      g.fill();
      g.fillStyle = m.color;
      g.beginPath();
      g.arc(px, py, 13, 0, Math.PI * 2);
      g.fill();
    }

    // the player: an arrow showing which way they face
    g.rotate(yaw - facing);
    g.fillStyle = '#14171c';
    g.beginPath();
    g.moveTo(0, -19);
    g.lineTo(13, 14);
    g.lineTo(0, 7);
    g.lineTo(-13, 14);
    g.closePath();
    g.fill();
    g.fillStyle = '#f2b705';
    g.beginPath();
    g.moveTo(0, -14);
    g.lineTo(9, 10);
    g.lineTo(0, 4.5);
    g.lineTo(-9, 10);
    g.closePath();
    g.fill();

    // the N marker rides the rim toward north
    const r = (this.node.clientWidth || SIZE) / 2 - 13;
    this.north.style.transform = `translate(${Math.sin(turn) * r}px, ${-Math.cos(turn) * r}px)`;
  }

  refreshNear(x, z) {
    this.nearAt = { x, z };
    const reach = this.range * 1.6;
    const list = [];
    for (const p of this.places) {
      if (Math.abs(p.x - x) > reach || Math.abs(p.z - z) > reach) continue;
      if (BLIP[p.k] || p.k === 'quarter' || p.k === 'village' || p.k === 'town') list.push(p);
    }
    // names first, then markers, so markers draw on top
    list.sort((a, b) => (BLIP[a.k] ? 1 : 0) - (BLIP[b.k] ? 1 : 0));
    this.near = list.slice(0, 60);
  }
}
